import { useEffect, useRef, useState, ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ScrollRevealSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
}

export const ScrollRevealSection = ({ children, className, delay = 0 }: ScrollRevealSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '-50px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={cn(
        'transition-all duration-700 ease-out',
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10',
        className
      )}
    >
      {children}
    </div>
  );
};

interface AnimatedDividerProps {
  icon?: ReactNode;
  label?: string;
  sublabel?: string;
}

export const AnimatedDivider = ({ icon, label, sublabel }: AnimatedDividerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lineProgress, setLineProgress] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate line progress
          let progress = 0;
          const interval = setInterval(() => {
            progress += 2;
            setLineProgress(Math.min(progress, 100));
            if (progress >= 100) clearInterval(interval);
          }, 10);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative py-16 sm:py-20 overflow-hidden">
      {/* Animated gradient lines */}
      <div className="absolute inset-x-0 top-1/2 h-px">
        <div 
          className="h-full bg-gradient-to-r from-transparent via-primary/60 to-transparent transition-all duration-1000"
          style={{ 
            opacity: isVisible ? 1 : 0,
            transform: `scaleX(${lineProgress / 100})`,
          }}
        />
      </div>
      
      {/* Secondary animated lines */}
      <div 
        className={cn(
          "absolute left-0 top-1/2 h-[2px] bg-gradient-to-r from-primary/20 via-primary/40 to-transparent",
          "transition-all duration-1000 delay-200",
          isVisible ? "w-1/3 opacity-100" : "w-0 opacity-0"
        )}
      />
      <div 
        className={cn(
          "absolute right-0 top-1/2 h-[2px] bg-gradient-to-l from-primary/20 via-primary/40 to-transparent",
          "transition-all duration-1000 delay-200",
          isVisible ? "w-1/3 opacity-100" : "w-0 opacity-0"
        )}
      />

      {/* Central element */}
      <div className="relative flex flex-col items-center justify-center">
        {/* Icon container */}
        <div 
          className={cn(
            "relative z-10 w-14 h-14 rounded-2xl bg-background border border-primary/40 flex items-center justify-center",
            "transition-all duration-700",
            isVisible 
              ? "opacity-100 scale-100 rotate-0" 
              : "opacity-0 scale-50 -rotate-12"
          )}
        >
          <div className="text-primary">
            {icon}
          </div>
          
          {/* Pulse ring */}
          <div 
            className={cn(
              "absolute inset-0 rounded-2xl border-2 border-primary/30",
              "transition-all duration-1000",
              isVisible ? "animate-ping opacity-0" : "opacity-0"
            )}
          />
        </div>

        {/* Label */}
        {label && (
          <div 
            className={cn(
              "mt-4 text-center transition-all duration-500 delay-300",
              isVisible 
                ? "opacity-100 translate-y-0" 
                : "opacity-0 translate-y-4"
            )}
          >
            <p className="text-xs font-semibold text-primary uppercase tracking-[0.2em]">
              {label}
            </p>
            {sublabel && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {sublabel}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Floating particles */}
      {isVisible && (
        <>
          <div className="absolute left-1/4 top-1/2 w-1 h-1 rounded-full bg-primary/50 animate-pulse" 
               style={{ animationDelay: '0.2s' }} />
          <div className="absolute right-1/4 top-1/2 w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse" 
               style={{ animationDelay: '0.5s' }} />
          <div className="absolute left-1/3 top-1/3 w-1 h-1 rounded-full bg-primary/30 animate-pulse" 
               style={{ animationDelay: '0.8s' }} />
          <div className="absolute right-1/3 bottom-1/3 w-1 h-1 rounded-full bg-primary/35 animate-pulse" 
               style={{ animationDelay: '1s' }} />
        </>
      )}
    </div>
  );
};

interface SectionHeaderProps {
  badge?: string;
  badgeIcon?: ReactNode;
  title: ReactNode;
  subtitle?: string;
}

export const AnimatedSectionHeader = ({ badge, badgeIcon, title, subtitle }: SectionHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="text-center mb-12">
      {badge && (
        <div 
          className={cn(
            "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-5",
            "transition-all duration-500",
            isVisible 
              ? "opacity-100 translate-y-0 scale-100" 
              : "opacity-0 translate-y-4 scale-95"
          )}
        >
          {badgeIcon}
          <span className="text-xs font-medium text-primary uppercase tracking-wider">{badge}</span>
        </div>
      )}
      <h2 
        className={cn(
          "text-2xl sm:text-3xl lg:text-4xl font-bold mb-4",
          "transition-all duration-600 delay-100",
          isVisible 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 translate-y-6"
        )}
      >
        {title}
      </h2>
      {subtitle && (
        <p 
          className={cn(
            "text-muted-foreground max-w-lg mx-auto text-sm sm:text-base",
            "transition-all duration-700 delay-200",
            isVisible 
              ? "opacity-100 translate-y-0" 
              : "opacity-0 translate-y-4"
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};
